import { useState } from "react";
import { useTranslation } from "react-i18next";

const useValidation = () => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});

  const validateRequired = (value, fieldName) => {
    const stringValue = value?.toString?.() ?? "";
    if (stringValue.trim() == "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: t("validation.required"),
      }));
      return false;
    }
    setErrors((prevErrors) => {
      const { [fieldName]: removed, ...rest } = prevErrors;
      return rest;
    });
    return true;
  };

  const validateLength = (value, fieldName, lengthInfo) => {
    const { min, max, exact_length } = lengthInfo;
    const valStr = value?.toString?.() ?? "";
    let errorMessage = "";

    if (exact_length > 0 && valStr.length != exact_length) {
      errorMessage = t("validation.exact_length", { length: exact_length });
    } else if (min > 0 && valStr.length < min) {
      errorMessage = t("validation.min_length", { min });
    } else if (max > 0 && valStr.length > max) {
      errorMessage = t("validation.max_length", { max });
    }

    if (errorMessage) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: errorMessage,
      }));
      return false;
    }

    setErrors((prevErrors) => {
      const { [fieldName]: removed, ...rest } = prevErrors;
      return rest;
    });
    return true;
  };

  const validateField = (fieldName, value, validations = [], options = {}) => {
    let errorMessage = null;
    const valStr = value?.toString?.() ?? "";

    for (let validation of validations) {
      switch (validation) {
        case "required":
          if (valStr.trim() == "") {
            errorMessage = t("validation.required");
          }
          break;

        case "email":
          if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(valStr)) {
            errorMessage = t("validation.invalidEmail");
          }
          break;

        case "name":
          if (!/^[a-zA-Z\s]+$/.test(valStr)) {
            errorMessage = t("validation.invalidName");
          }
          break;

        case "number":
          if (!/^[0-9]+$/.test(valStr)) {
            errorMessage = t("validation.invalidNumber");
          }
          break;

        case "date":
          if (!/^\d{2}-\d{2}-\d{4}$/.test(valStr)) {
            errorMessage = t("validation.invalidDate");
          }
          break;

        case "select":
        case "tab":
          if (valStr == "") {
            errorMessage = t("validation.selectOption");
          }
          break;

        case "min":
          if (parseFloat(valStr) < options.min_value) {
            errorMessage = t("validation.minValue", { min: options.min_value });
          }
          break;

        case "max":
          if (parseFloat(valStr) > options.max_value) {
            errorMessage = t("validation.maxValue", { max: options.max_value });
          }
          break;

        case "length":
          const { min, max, exact_length } = options;
          if (exact_length > 0 && valStr.length != exact_length) {
            errorMessage = t("validation.exact_length", { length: exact_length });
          } else if (min > 0 && valStr.length < min) {
            errorMessage = t("validation.min_length", { min });
          } else if (max > 0 && valStr.length > max) {
            errorMessage = t("validation.max_length", { max });
          }
          break;

        case "password":
          const hasUppercase = /[A-Z]/.test(valStr);
          const hasLowercase = /[a-z]/.test(valStr);
          const hasNumber = /[0-9]/.test(valStr);
          const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(valStr);
          if (
            valStr.length < 8 ||
            !hasUppercase ||
            !hasLowercase ||
            !hasNumber ||
            !hasSymbol
          ) {
            errorMessage =
              "Password should be at least 8 characters long and should contain one uppercase, one lowercase, one symbol and one number";
          }
          break;

        default:
          break;
      }

      if (errorMessage) break; // stop on first failure
    }

    if (errorMessage) {
      setErrors((prev) => ({ ...prev, [fieldName]: errorMessage }));
      return false;
    } else {
      setErrors((prevErrors) => {
        const { [fieldName]: removed, ...rest } = prevErrors;
        return rest;
      });
      return true;
    }
  };

  const validateFields = (validationConfig, values) => {
    const newErrors = {};
    validationConfig.forEach(({ name, validations, min_value, max_value, min, max, exact_length }) => {
      validations.forEach((validation) => {
        const value = values?.[name];
        const valStr = value?.toString?.() ?? "";
        let errorMessage = null;

        switch (validation) {
          case "file":
            if (!value || (Array.isArray(value) && !value.length)) {
              errorMessage = t("validation.required");
            }
            break;

          case "required":
            if (valStr.trim() == "") {
              errorMessage = t("validation.required");
            }
            break;

          case "email":
            if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(valStr)) {
              errorMessage = t("validation.invalidEmail");
            }
            break;

          case "name":
            if (!/^[a-zA-Z\s]+$/.test(valStr)) {
              errorMessage = t("validation.invalidName");
            }
            break;

          case "number":
            if (!/^[0-9]+$/.test(valStr)) {
              errorMessage = t("validation.invalidNumber");
            }
            break;

          case "date":
            if (!/^\d{2}-\d{2}-\d{4}$/.test(valStr)) {
              errorMessage = t("validation.invalidDate");
            }
            break;

          case "select":
          case "tab":
            if (valStr == "") {
              errorMessage = t("validation.selectOption");
            }
            break;

          case "min":
            if (parseFloat(valStr) < min_value) {
              errorMessage = t("validation.minValue", { min: min_value });
            }
            break;

          case "max":
            if (parseFloat(valStr) > max_value) {
              errorMessage = t("validation.maxValue", { max: max_value });
            }
            break;

          case "length":
            if (exact_length > 0 && valStr.length != exact_length) {
              errorMessage = t("validation.exact_length", { length: exact_length });
            } else if (min > 0 && valStr.length < min) {
              errorMessage = t("validation.min_length", { min });
            } else if (max > 0 && valStr.length > max) {
              errorMessage = t("validation.max_length", { max });
            }
            break;

          case "password":
            const hasUppercase = /[A-Z]/.test(valStr);
            const hasLowercase = /[a-z]/.test(valStr);
            const hasNumber = /[0-9]/.test(valStr);
            const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(valStr);
            if (
              valStr.length < 8 ||
              !hasUppercase ||
              !hasLowercase ||
              !hasNumber ||
              !hasSymbol
            ) {
              errorMessage =
                "Password should be at least 8 characters long and should contain one uppercase, one lowercase, one symbol and one number";
            }
            break;

          default:
            break;
        }

        if (errorMessage) {
          newErrors[name] = errorMessage;
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length == 0;
  };

  const isValid = Object.keys(errors).length == 0;

  const setFieldError = (fieldName, errorMessage) => {
    if (errorMessage) {
      setErrors((prev) => ({ ...prev, [fieldName]: errorMessage }));
    } else {
      setErrors((prev) => {
        const { [fieldName]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  return {
    errors,
    validateField,
    validateFields,
    validateRequired,
    validateLength,
    setFieldError,
    isValid,
  };
};

export default useValidation;
